<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 md:p-4">
    <div class="bg-white shadow-xl md:rounded-lg w-full md:max-w-7xl md:max-h-[95vh] h-full max-h-screen overflow-hidden rounded-none flex flex-col min-h-0">
      <!-- Header -->
      <div class="bg-brown-600 text-white p-4 md:p-6 sticky top-0 z-10">
        <div class="flex items-center justify-between flex-col md:flex-row gap-2">
          <div>
            <h2 class="text-xl font-semibold">سفارش {{ table?.name }}</h2>
            <p class="text-brown-100 text-sm">{{ table?.sectionName }}</p>
          </div>
          <div class="flex items-center gap-3">
            <!-- Mobile quick add item button -->
            <button 
              @click="showItemPicker = true"
              class="md:hidden bg-white/20 hover:bg-white/30 text-white px-3 py-1 rounded-lg text-sm font-medium transition-colors"
            >
              + افزودن محصول
            </button>
            <button 
            @click="$emit('close')"
            class="text-white hover:text-brown-100 transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 flex flex-col md:flex-row h-full md:h:[calc(95vh-200px)] min-h-0">
        <!-- Left Side - Menu Items -->
        <div class="hidden md:block md:w-2/5 border-l border-gray-200 overflow-y-auto">
          <div class="p-6">
            <h3 class="text-lg font-semibold text-gray-800 mb-4">افزودن محصول</h3>
            
            <!-- Search -->
            <div class="mb-4">
              <input 
                v-model="searchQuery"
                type="text"
                placeholder="جستجو در منو..."
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
              />
            </div>

            <!-- Categories -->
            <div class="mb-4">
              <div class="flex space-x-2 space-x-reverse overflow-x-auto pb-2">
                <button 
                  v-for="category in categories"
                  :key="category.id"
                  @click="selectedCategory = category"
                  :class="[
                    'px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors',
                    selectedCategory?.id === category.id 
                      ? 'bg-brown-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  ]"
                >
                  {{ category.name }}
                </button>
              </div>
            </div>

            <!-- Menu Items -->
            <div class="space-y-3">
              <div 
                v-for="item in filteredMenuItems"
                :key="item.id"
                @click="addToOrder(item)"
                class="flex items-center space-x-3 space-x-reverse p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <img 
                  :src="item.image" 
                  :alt="item.name"
                  class="w-16 h-16 object-cover rounded-lg"
                />
                <div class="flex-1">
                  <h4 class="font-medium text-gray-800">{{ item.name }}</h4>
                  <p class="text-sm text-gray-500">{{ item.description }}</p>
                  <p class="text-sm font-medium text-brown-600">{{ formatPrice(item.price) }}</p>
                </div>
                <button class="text-brown-600 hover:text-brown-700">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Side - Order Details -->
        <div class="md:w-3/5 flex flex-col flex-1 min-h-0">
          <div class="p-4 md:p-6 flex-1 overflow-y-auto pb-28 min-h-0">
            <h3 class="text-lg font-semibold text-gray-800 mb-4">جزئیات سفارش</h3>
            
            <!-- Order Notes -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">یادداشت</label>
              <textarea 
                v-model="orderNotes"
                @blur="updateOrderNotes"
                rows="3"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
                placeholder="یادداشت سفارش..."
              ></textarea>
            </div>

            <!-- Delivery Information for Pickup Orders -->
            <div v-if="order?.isPickup" class="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 class="text-sm font-medium text-blue-800 mb-3">اطلاعات تحویل</h4>
              
              <div class="space-y-3">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">انتخاب مشتری</label>
                  <select
                    v-model="selectedCustomerId"
                    @change="handleCustomerChange"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option :value="null">بدون مشتری</option>
                    <option v-for="c in customers" :key="c.id" :value="c.id">
                      {{ c.fullName }}
                      <span v-if="c.customerNumber"> - {{ c.customerNumber }}</span>
                      <span v-if="c.phoneNumber"> - {{ c.phoneNumber }}</span>
                    </option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">آدرس تحویل</label>
                  <input 
                    v-model="deliveryAddress"
                    @blur="updateDeliveryAddress"
                    type="text"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="آدرس تحویل..."
                  />
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">هزینه تحویل (تومان)</label>
                  <input 
                    v-model="deliveryFee"
                    @blur="updateDeliveryFee"
                    type="number"
                    min="0"
                    step="1000"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            <!-- Discount Percentage -->
            <div class="mb-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <h4 class="text-sm font-medium text-yellow-800 mb-3">تخفیف</h4>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">درصد تخفیف (%)</label>
                <input 
                  v-model.number="discountPercentage"
                  @blur="updateDiscountPercentage"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="0"
                />
              </div>
            </div>

            <!-- Order Items -->
            <h3 class="text-lg font-semibold text-gray-800 mb-3 mt-6">آیتم‌های سفارش</h3>
            <div class="space-y-3">
            <div 
              v-for="item in order?.orderItems"
                :key="item.id"
                class="flex items-center space-x-3 space-x-reverse p-3 border border-gray-200 rounded-lg"
              >
                <img 
                  :src="item.menuItemImage" 
                  :alt="item.menuItemName"
                  class="w-12 h-12 object-cover rounded-lg"
                />
                <div class="flex-1">
                  <h4 class="font-medium text-gray-800">{{ item.menuItemName }}</h4>
                  <p class="text-sm text-gray-500">{{ formatPrice(item.unitPrice) }}</p>
                </div>
                <div class="flex items-center space-x-2 space-x-reverse">
                  <button 
                    @click="updateItemQuantity(item, item.quantity - 1)"
                    :disabled="item.quantity <= 1"
                    class="w-8 h-8 rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"></path>
                    </svg>
                  </button>
                  <span class="w-8 text-center font-medium">{{ item.quantity }}</span>
                  <button 
                    @click="updateItemQuantity(item, item.quantity + 1)"
                    class="w-8 h-8 rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300 flex items-center justify-center"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                  </button>
                </div>
                <div class="text-right">
                  <p class="font-medium text-gray-800">{{ formatPrice(item.totalPrice) }}</p>
                  <button 
                    @click="removeOrderItem(item)"
                    class="text-red-600 hover:text-red-700 text-sm"
                  >
                    حذف
                  </button>
                </div>
              </div>
            </div>

            <!-- Empty Order -->
            <div v-if="!order?.orderItems || order?.orderItems.length === 0" class="text-center py-8 text-gray-500">
              <div class="text-4xl mb-2">🍽️</div>
              <p>هیچ محصولی در سفارش وجود ندارد</p>
              <div class="mt-4">
                <button 
                  @click="showItemPicker = true"
                  class="md:hidden inline-flex items-center bg-brown-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brown-700 transition-colors"
                >
                  + افزودن محصول
                </button>
              </div>
            </div>
          </div>

                    <!-- Footer -->
          <div class="border-t border-gray-200 p-4 md:p-6 sticky bottom-0 bg-white">
            <!-- Mobile: Add Item Button -->
            <button 
              @click="showItemPicker = true"
              class="block md:hidden w-full mb-3 bg-brown-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-brown-700 transition-colors"
            >
              افزودن محصول
            </button>
            <!-- Subtotal -->
            <div class="flex items-center justify-between mb-2 text-sm text-gray-500">
              <span>مجموع محصولات:</span>
              <span>{{ formatPrice(order?.subtotalAmount || calculatedTotal) }}</span>
            </div>
            
            <!-- Tax -->
            <div v-if="order?.taxAmount > 0" class="flex items-center justify-between mb-2 text-sm text-orange-600">
              <span>مالیات ({{ order?.taxPercentage }}%):</span>
              <span>{{ formatPrice(order?.taxAmount || 0) }}</span>
            </div>

            <!-- Discount Row -->
            <div v-if="(order?.discountPercentage || discountPercentage) > 0" class="flex items-center justify-between mb-2 text-sm text-green-700">
              <span>تخفیف ({{ order?.discountPercentage ?? discountPercentage }}%):</span>
              <span>- {{ formatPrice(discountAmount) }}</span>
            </div>
            
            <!-- Delivery Fee for Pickup Orders -->
            <div v-if="order?.isPickup && deliveryFee > 0" class="flex items-center justify-between mb-2 text-sm text-blue-600">
              <span>هزینه تحویل:</span>
              <span>{{ formatPrice(deliveryFee) }}</span>
            </div>
            
            <!-- Total -->
            <div class="flex items-center justify-between mb-4 pt-2 border-t border-gray-200">
              <span class="text-lg font-semibold text-gray-800">مجموع کل:</span>
              <span class="text-2xl font-bold text-brown-600">{{ formatPrice(order?.totalAmount || (calculatedTotal + (order?.isPickup ? deliveryFee : 0))) }}</span>
            </div>
            
                                     <!-- Internal Order Buttons -->
            <div v-if="order?.isInternalOrder" class="grid grid-cols-2 gap-3">
              <button 
                @click="cancelOrder"
                class="bg-red-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-red-700 transition-colors"
              >
                انصراف
              </button>
              <button 
                @click="saveInternalOrder"
                class="bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors"
              >
                ذخیره
              </button>
            </div>
            
            <!-- Regular Order Buttons -->
            <div v-else class="grid grid-cols-2 md:grid-cols-3 gap-3">
              <button 
                @click="printOrder"
                class="bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                🖨️ چاپ سفارش
              </button>
              <button 
                v-if="order?.status === 0"
                @click="markAsUnpaid"
                class="bg-orange-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-orange-700 transition-colors"
              >
                علامت‌گذاری به عنوان پرداخت نشده
              </button>
              <button 
                v-if="order?.status === 3"
                @click="closeUnpaidOrder"
                class="bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors"
              >
                بستن سفارش (حفظ وضعیت پرداخت نشده)
              </button>
              <button 
                v-if="order?.status === 0"
                @click="closeOrder"
                class="bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors"
              >
                بستن سفارش
              </button>
              <button 
                @click="cancelOrder"
                class="bg-red-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-red-700 transition-colors"
              >
                لغو سفارش
              </button>
              <button 
                @click="$emit('close')"
                class="bg-gray-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-700 transition-colors"
              >
                بازگشت
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile Item Picker Overlay -->
      <div v-if="showItemPicker" class="fixed inset-0 bg-white z-50 flex flex-col md:hidden">
        <div class="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 class="text-lg font-semibold text-gray-800">افزودن محصول</h3>
          <button @click="showItemPicker = false" class="text-gray-600 hover:text-gray-800">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        <div class="p-4 flex-1 overflow-y-auto">
          <!-- Search -->
          <div class="mb-4">
            <input 
              v-model="searchQuery"
              type="text"
              placeholder="جستجو در منو..."
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
            />
          </div>

          <!-- Categories -->
          <div class="mb-4">
            <div class="flex space-x-2 space-x-reverse overflow-x-auto pb-2">
              <button 
                v-for="category in categories"
                :key="category.id"
                @click="selectedCategory = category"
                :class="[
                  'px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors',
                  selectedCategory?.id === category.id 
                    ? 'bg-brown-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                ]"
              >
                {{ category.name }}
              </button>
            </div>
          </div>

          <!-- Menu Items -->
          <div class="space-y-3">
            <div 
              v-for="item in filteredMenuItems"
              :key="item.id"
              @click="selectItemFromPicker(item)"
              class="flex items-center space-x-3 space-x-reverse p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              <img 
                :src="item.image" 
                :alt="item.name"
                class="w-16 h-16 object-cover rounded-lg"
              />
              <div class="flex-1">
                <h4 class="font-medium text-gray-800">{{ item.name }}</h4>
                <p class="text-sm text-gray-500">{{ item.description }}</p>
                <p class="text-sm font-medium text-brown-600">{{ formatPrice(item.price) }}</p>
              </div>
              <button class="text-brown-600 hover:text-brown-700">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Payment Method Modal -->
    <PaymentMethodModal 
      v-if="showPaymentModal"
      :order-id="order?.id"
      :total-amount="calculatedTotal"
      @close="closePaymentModal"
      @confirm="handlePaymentConfirm"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { customersAPI } from '@/services/api'
import { printOrder as printOrderUtil } from '@/utils/printUtils'
import PaymentMethodModal from './PaymentMethodModal.vue'
import { localOrders, localCategories, localMenuItems } from '@/services/localDb'

export default {
  name: 'OrderModal',
  components: {
    PaymentMethodModal
  },
  props: {
    order: {
      type: Object,
      required: true
    },
    table: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'order-updated'],
  setup(props, { emit }) {
    const categories = ref([])
    const menuItems = ref([])
    const selectedCategory = ref(null)
    const searchQuery = ref('')
    const orderNotes = ref('')
    const loading = ref(false)
    const showPaymentModal = ref(false)
    const showItemPicker = ref(false)

    // Pickup/Delivery variables
    const deliveryAddress = ref('')
    const deliveryFee = ref(0)
    const customers = ref([])
    const selectedCustomerId = ref(null)

    // Discount
    const discountPercentage = ref(0)

    const filteredMenuItems = computed(() => {
      let items = menuItems.value

      // Filter by category
      if (selectedCategory.value) {
        // Compare item.categoryId (server category ID) with category.serverId
        // Menu items store categoryId as the server's category ID, so we need to compare
        // with the category's serverId, not its local database id
        const categoryServerId = selectedCategory.value.serverId || selectedCategory.value.id
        items = items.filter(item => {
          // item.categoryId is the server's category ID
          return item.categoryId === categoryServerId
        })
      }

      // Filter by search query
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        items = items.filter(item => 
          item.name.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query)
        )
      }

      return items
    })

    const calculatedTotal = computed(() => {
      if (!props.order?.orderItems) return 0
      return props.order.orderItems.reduce((sum, item) => sum + (Number(item.totalPrice) || 0), 0)
    })

    const discountAmount = computed(() => {
      const base = Number(props.order?.subtotalAmount ?? calculatedTotal.value) || 0
      let pct = Number(props.order?.discountPercentage ?? discountPercentage.value)
      if (!isFinite(pct) || pct < 0) pct = 0
      if (pct > 100) pct = 100
      return Math.round((base * (pct / 100)) * 100) / 100
    })

    const loadCategories = async () => {
      try {
        // Use local database for offline functionality
        categories.value = await localCategories.listActive()
        if (categories.value.length > 0) {
          selectedCategory.value = categories.value[0]
        }
      } catch (err) {
        console.error('Error loading categories:', err)
        categories.value = []
      }
    }

    const loadCustomers = async () => {
      try {
        const res = await customersAPI.getAll()
        customers.value = res.data || []
      } catch (err) {
        console.error('Error loading customers:', err)
      }
    }

    const loadMenuItems = async () => {
      try {
        // Use local database for offline functionality
        menuItems.value = await localMenuItems.listActive()
      } catch (err) {
        console.error('Error loading menu items:', err)
        menuItems.value = []
      }
    }

    const addToOrder = async (menuItem) => {
      try {
        loading.value = true
        
        // Ensure we have a local order id; create/resolve if missing
        let orderId = Number(props.order?.id)
        if (!Number.isFinite(orderId)) {
          const rawTableId = props.table?.id ?? props.order?.tableId
          const tableName = props.table?.name || 'میز'

          // If pickup/internal virtual ids like "pickup-123" / "internal-123"
          if (typeof rawTableId === 'string' && (rawTableId.startsWith('pickup-') || rawTableId.startsWith('internal-'))) {
            const candidateId = Number(String(rawTableId).split('-')[1])
            if (Number.isFinite(candidateId)) {
              orderId = candidateId
            }
          }

          // If still missing, try numeric table id path
          if (!Number.isFinite(orderId)) {
            const tableIdNum = Number(rawTableId)
            if (Number.isFinite(tableIdNum)) {
              // Try to find existing local open/unpaid order for this table
              const existing = await localOrders.getByTableId(tableIdNum)
              if (existing?.id) {
                orderId = existing.id
              } else {
                const created = await localOrders.createTableOrder(tableIdNum, tableName)
                orderId = created.id
                emit('order-updated', created)
              }
            }
          }

          if (!Number.isFinite(orderId)) {
            throw new Error('Order ID could not be resolved')
          }
        }
        
        // Validate menu item id
        const menuItemId = Number(menuItem?.id ?? menuItem?.serverId)
        if (!Number.isFinite(menuItemId)) {
          throw new Error('Menu item is missing or invalid id')
        }
        
        console.log('Adding item to order:', { orderId, menuItemId })
        
        // All orders use local database for offline functionality
        await localOrders.addItem(orderId, { ...menuItem, id: menuItemId })
        const refreshed = await localOrders.getById(orderId)
        if (!refreshed) {
          throw new Error('Order not found in local database after adding item')
        }
        // Emit event to update order instead of mutating prop
        emit('order-updated', refreshed)
      } catch (err) {
        console.error('Error adding item to order:', err)
        console.error('Order data:', props.order)
        console.error('Menu item data:', menuItem)
        alert('خطا در افزودن محصول: ' + err.message)
      } finally {
        loading.value = false
      }
    }

    const selectItemFromPicker = async (menuItem) => {
      await addToOrder(menuItem)
      showItemPicker.value = false
      // Ensure UI refresh: local changes already applied above
    }

    const updateItemQuantity = async (orderItem, newQuantity) => {
      if (newQuantity < 1) return

      try {
        loading.value = true
        await localOrders.updateItemQuantity(orderItem.id, newQuantity)
        const refreshed = await localOrders.getById(props.order.id)
        emit('order-updated', refreshed)
      } catch (err) {
        console.error('Error updating item quantity:', err)
        alert('خطا در بروزرسانی تعداد')
      } finally {
        loading.value = false
      }
    }

    const removeOrderItem = async (orderItem) => {
      try {
        loading.value = true
        await localOrders.removeItem(orderItem.id)
        const refreshed = await localOrders.getById(props.order.id)
        emit('order-updated', refreshed)
      } catch (err) {
        console.error('Error removing item:', err)
        alert('خطا در حذف محصول')
      } finally {
        loading.value = false
      }
    }

    const updateOrderNotes = async () => {
      try {
        // Use local database for offline functionality
        await localOrders.updateOrder(props.order.id, {
          notes: orderNotes.value,
          status: props.order.status
        })
        // Emit updated order to parent
        const updatedOrder = await localOrders.getById(props.order.id)
        if (updatedOrder) {
          emit('order-updated', updatedOrder)
        }
      } catch (err) {
        console.error('Error updating order notes:', err)
      }
    }

    const updateDeliveryAddress = async () => {
      try {
        // Use local database for offline functionality
        await localOrders.updateOrder(props.order.id, {
          notes: props.order.notes,
          status: props.order.status,
          deliveryAddress: deliveryAddress.value
        })
        // Emit updated order to parent
        const updatedOrder = await localOrders.getById(props.order.id)
        if (updatedOrder) {
          emit('order-updated', updatedOrder)
        }
      } catch (err) {
        console.error('Error updating delivery address:', err)
      }
    }

    const updateDeliveryFee = async () => {
      try {
        const fee = Number(deliveryFee.value) || 0
        // Use local database for offline functionality
        await localOrders.updateOrder(props.order.id, {
          notes: props.order.notes,
          status: props.order.status,
          deliveryFee: fee
        })
        // Emit updated order to parent
        const updatedOrder = await localOrders.getById(props.order.id)
        if (updatedOrder) {
          emit('order-updated', updatedOrder)
        }
      } catch (err) {
        console.error('Error updating delivery fee:', err)
      }
    }

    const updateDiscountPercentage = async () => {
      try {
        let pct = Number(discountPercentage.value)
        if (!isFinite(pct) || pct < 0) pct = 0
        if (pct > 100) pct = 100
        // Use local database for offline functionality
        await localOrders.updateOrder(props.order.id, {
          notes: props.order.notes,
          status: props.order.status,
          discountPercentage: pct
        })
        // Emit updated order to parent
        const updatedOrder = await localOrders.getById(props.order.id)
        if (updatedOrder) {
          emit('order-updated', updatedOrder)
        }
      } catch (err) {
        console.error('Error updating discount percentage:', err)
      }
    }

    const handleCustomerChange = async () => {
      try {
        // Use local database for offline functionality
        await localOrders.updateOrder(props.order.id, {
          notes: props.order.notes,
          status: props.order.status,
          customerId: selectedCustomerId.value
        })

        const chosen = customers.value.find(c => c.id === selectedCustomerId.value)
        if (chosen && chosen.address && !deliveryAddress.value) {
          deliveryAddress.value = chosen.address
          await updateDeliveryAddress()
        }
        
        // Emit updated order to parent
        const updatedOrder = await localOrders.getById(props.order.id)
        if (updatedOrder) {
          emit('order-updated', updatedOrder)
        }
      } catch (err) {
        console.error('Error updating customer:', err)
      }
    }

    const closeOrder = async () => {
      showPaymentModal.value = true
    }

    const handlePaymentConfirm = async (paymentData) => {
      try {
        loading.value = true
        
        // Use local database to close order (offline functionality)
        await localOrders.closeOrder(props.order.id, {
          notes: orderNotes.value,
          paymentMethod: paymentData.method,
          cashAmount: paymentData.mixedPayment?.cash,
          posAmount: paymentData.mixedPayment?.pos,
          cardTransferAmount: paymentData.mixedPayment?.card
        })
        
        // Get the updated order from local database
        const updatedOrder = await localOrders.getById(props.order.id)
        if (!updatedOrder) {
          throw new Error('Order not found in local database after closing')
        }
        
        showPaymentModal.value = false
        emit('order-updated', updatedOrder)
        emit('close')
      } catch (err) {
        console.error('Error closing order:', err)
        alert('خطا در بستن سفارش: ' + err.message)
      } finally {
        loading.value = false
      }
    }

    const closePaymentModal = () => {
      showPaymentModal.value = false
    }

    const cancelOrder = async () => {
      if (!confirm('آیا از لغو سفارش اطمینان دارید؟')) return

      try {
        loading.value = true
        // Use local database for offline functionality
        await localOrders.updateOrder(props.order.id, {
          notes: orderNotes.value,
          status: 2 // Cancelled
        })
        
        // Emit updated order to parent
        const updatedOrder = await localOrders.getById(props.order.id)
        if (updatedOrder) {
          emit('order-updated', updatedOrder)
        }
        emit('close')
      } catch (err) {
        console.error('Error cancelling order:', err)
        alert('خطا در لغو سفارش')
      } finally {
        loading.value = false
      }
    }

    const markAsUnpaid = async () => {
      if (!confirm('آیا از علامت‌گذاری سفارش به عنوان پرداخت نشده اطمینان دارید؟')) return

      try {
        loading.value = true
        // Use local database for offline functionality
        await localOrders.updateOrder(props.order.id, {
          notes: orderNotes.value,
          status: 3 // Unpaid
        })
        
        // Emit updated order to parent
        const updatedOrder = await localOrders.getById(props.order.id)
        if (updatedOrder) {
          emit('order-updated', updatedOrder)
        }
        emit('close')
      } catch (err) {
        console.error('Error marking order as unpaid:', err)
        alert('خطا در علامت‌گذاری سفارش')
      } finally {
        loading.value = false
      }
    }

    const closeUnpaidOrder = async () => {
      if (!confirm('آیا از بستن سفارش با حفظ وضعیت پرداخت نشده اطمینان دارید؟')) return

      try {
        loading.value = true
        // Use local database for offline functionality
        await localOrders.updateOrder(props.order.id, {
          notes: orderNotes.value,
          status: 3 // Keep as Unpaid but mark as closed
        })
        
        // Emit updated order to parent
        const updatedOrder = await localOrders.getById(props.order.id)
        if (updatedOrder) {
          emit('order-updated', updatedOrder)
        }
        emit('close')
      } catch (err) {
        console.error('Error closing unpaid order:', err)
        alert('خطا در بستن سفارش پرداخت نشده')
      } finally {
        loading.value = false
      }
    }

    const saveInternalOrder = async () => {
      try {
        loading.value = true
        
        // Use local database for offline functionality
        await localOrders.updateOrder(props.order.id, {
          notes: orderNotes.value,
          status: 3 // Keep as Unpaid
        })
        
        // Emit updated order to parent
        const updatedOrder = await localOrders.getById(props.order.id)
        if (updatedOrder) {
          emit('order-updated', updatedOrder)
        }
        emit('close')
      } catch (err) {
        console.error('Error saving internal order:', err)
        alert('خطا در ذخیره سفارش داخلی')
      } finally {
        loading.value = false
      }
    }

    const formatPrice = (price) => {
      const n = Number(price)
      if (!isFinite(n)) return '0 تومان'
      return new Intl.NumberFormat('fa-IR').format(n) + ' تومان'
    }

    const printOrder = () => {
      printOrderUtil(props.order, props.table)
    }

    // Watch for order changes to update notes and delivery info
    watch(() => props.order, (newOrder) => {
      if (newOrder) {
        orderNotes.value = newOrder.notes || ''
        deliveryAddress.value = newOrder.deliveryAddress || ''
        deliveryFee.value = Number(newOrder.deliveryFee) || 0
        selectedCustomerId.value = newOrder.customerId || null
        discountPercentage.value = Number(newOrder.discountPercentage) || 0
      }
    }, { immediate: true })

    onMounted(() => {
      loadCategories()
      loadMenuItems()
      loadCustomers()
    })

         return {
       categories,
       menuItems,
       selectedCategory,
       searchQuery,
       orderNotes,
       loading,
       showPaymentModal,
      filteredMenuItems,
       calculatedTotal,
      discountAmount,
       addToOrder,
      selectItemFromPicker,
       updateItemQuantity,
       removeOrderItem,
       updateOrderNotes,
       closeOrder,
       handlePaymentConfirm,
       closePaymentModal,
       cancelOrder,
       markAsUnpaid,
       closeUnpaidOrder,
       saveInternalOrder,
       printOrder,
       formatPrice,
       deliveryAddress,
       deliveryFee,
       updateDeliveryAddress,
      updateDeliveryFee,
      showItemPicker,
      discountPercentage,
      updateDiscountPercentage,
      customers,
      selectedCustomerId,
      handleCustomerChange
     }
  }
}
</script>

<style scoped>
/* Custom styles if needed */
</style>
